import { useEffect, useState, type ReactNode } from 'react'

import { Badge, Button, Dialog, DialogContent, DialogFooter } from '@aeris/ui'

import { readShellConfig, type ShellConfig } from '../../lib/shell-config.ts'

/**
 * "Sobre" — o que alguém precisa dizer ao telefone quando dá problema (F155).
 *
 * ## Por que isto existe
 *
 * O aplicativo instalado **não tem atualização automática** (F040): cada loja
 * fica na versão que instalou, e o suporte é por telefone. E não havia, em
 * lugar nenhum da interface, como saber **qual versão está rodando** — nem o
 * endereço do servidor, nem se há um Edge, nem onde fica o arquivo de log que
 * o troubleshooting manda enviar.
 *
 * O resultado era um atendimento que começava com três perguntas que ninguém
 * na loja sabia responder. Esta tela é a resposta das três, num lugar só, com
 * um botão de copiar — porque ditar um caminho de arquivo por telefone é como
 * não ter o arquivo.
 *
 * ## Por que não fala com o Tauri
 *
 * A página é servida por `http://127.0.0.1:<porta>`, que para o Tauri é uma
 * URL remota, e `invoke` é negado a essas. Tudo aqui vem de
 * `/__aeris/config`, que é a costura que já existia.
 */

/**
 * Onde o shell grava `aeris-desktop.log`.
 *
 * Montado aqui a partir do sistema operacional porque o caminho é resolvido no
 * Rust por `app_log_dir()`, e mandá-lo pelo config exigiria um campo novo para
 * uma informação que não muda. Se a pessoa estiver noutro sistema, a linha
 * some em vez de mostrar um caminho que não existe.
 */
const logHint = (): string | null => {
  const platform = navigator.userAgent
  if (/Windows/i.test(platform)) {
    return '%APPDATA%\\br.com.aerisone.desktop\\logs\\aeris-desktop.log'
  }
  if (/Linux/i.test(platform)) {
    return '~/.local/share/br.com.aerisone.desktop/logs/aeris-desktop.log'
  }
  return null
}

const Line = ({
  label,
  children,
}: {
  label: string
  children: ReactNode
}): ReactNode => (
  <>
    <dt className="text-(--text-muted)">{label}</dt>
    <dd className="break-all text-(--text-primary)">{children}</dd>
  </>
)

export const AboutDialog = ({
  onClose,
  onOpenServer,
}: {
  onClose: () => void
  /** Leva a `/servidor`. É o único caminho até lá no aplicativo instalado. */
  onOpenServer: () => void
}): ReactNode => {
  const [config, setConfig] = useState<ShellConfig | null | undefined>(
    undefined
  )
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    void readShellConfig(controller.signal).then((answer) => {
      if (!controller.signal.aborted) setConfig(answer)
    })
    return () => {
      controller.abort()
    }
  }, [])

  const log = logHint()

  /** O que o suporte pede, numa colagem só. */
  const summary = [
    `Aeris One ${config?.version ?? 'versão desconhecida'}`,
    `Servidor: ${config?.serverUrl === '' ? 'não configurado' : (config?.serverUrl ?? 'não é o aplicativo instalado')}`,
    `Edge: ${config?.edgeUrl === '' || config?.edgeUrl === undefined ? 'nenhum' : config.edgeUrl}`,
    config?.port == null ? null : `Porta local: ${String(config.port)}`,
    log === null ? null : `Log: ${log}`,
  ]
    .filter((line) => line !== null)
    .join('\n')

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        title="Sobre o Aeris One"
        description="O que o suporte precisa saber quando alguma coisa não vai bem."
      >
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-(length:--text-sm)">
            <Line label="Versão">
              {/*
                Um shell anterior à F155 não manda a versão. Dizer "não
                informada" é honesto; mostrar a do pacote web seria responder
                outra pergunta, porque é o instalador que fica velho.
              */}
              {config?.version ?? (
                <span className="text-(--text-muted)">
                  não informada por esta versão
                </span>
              )}
            </Line>

            {config === null ? (
              <Line label="Onde está rodando">
                <Badge tone="neutral">No navegador</Badge>
              </Line>
            ) : (
              <>
                <Line label="Servidor">
                  {config?.serverUrl === '' ? (
                    <span className="text-(--text-muted)">não configurado</span>
                  ) : (
                    (config?.serverUrl ?? '—')
                  )}
                </Line>
                <Line label="Aeris Edge">
                  {config?.edgeUrl === '' || config?.edgeUrl === undefined ? (
                    <span className="text-(--text-muted)">
                      nenhum — a loja não vende com a internet fora
                    </span>
                  ) : (
                    config.edgeUrl
                  )}
                </Line>
                {config?.port == null ? null : (
                  <Line label="Porta local">{config.port}</Line>
                )}
              </>
            )}

            {log === null ? null : (
              <Line label="Arquivo de log">
                <span className="font-(--font-mono) text-(length:--text-xs)">
                  {log}
                </span>
              </Line>
            )}
          </dl>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void navigator.clipboard
                  .writeText(summary)
                  .then(() => {
                    setCopied(true)
                  })
                  .catch(() => {
                    // Área de transferência negada é um botão que não faz nada,
                    // não uma tela que quebra: o texto continua na tela.
                  })
              }}
            >
              {copied ? 'Copiado' : 'Copiar para o suporte'}
            </Button>
            {config === null ? null : (
              <Button variant="ghost" size="sm" onClick={onOpenServer}>
                Trocar servidor ou Edge
              </Button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
